import { Avatar, Box, ClickAwayListener, Paper, Stack, TextField, Typography, styled } from "@mui/material";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import IUser from "../../models/user";
import { canEdit } from "../../utils/permissions";
import FieldLabel from "../input/v2/FieldLabel";
import EditOnly from "../permissions/EditOnly";
import PrimaryChip from "../input/v2/PrimaryChip";

const SectionTitle = styled(Typography)(() => ({
  fontWeight: 800,
  fontSize: 16,
}));

const userSearchFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: 14,
    height: 44,
    backgroundColor: "#fff",
    "& fieldset": { borderColor: "rgba(0,0,0,0.12)" },
  },
};

interface RouteAssignmentSectionProps {
  availableUsers: IUser[];
  selectedUsers: IUser[];
  userSearchText: string;
  onUserSearchTextChange: (value: string) => void;
  onAddUser: (user: IUser) => void;
  onRemoveUser: (userId: number) => void;
}

const RouteAssignmentSection = ({
  availableUsers,
  selectedUsers,
  userSearchText,
  onUserSearchTextChange,
  onAddUser,
  onRemoveUser,
}: RouteAssignmentSectionProps) => {
  const { t } = useTranslation();
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  const filteredUsers = useMemo(() => {
    const query = userSearchText.trim().toLowerCase();
    const selectedIds = new Set(selectedUsers.map((u) => u.id));

    return availableUsers
      .filter((user) => !selectedIds.has(user.id))
      .filter((user) => (!query ? true : user.name.toLowerCase().includes(query)))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [availableUsers, selectedUsers, userSearchText]);

  const openDropdown = () => setShowUserDropdown(true);
  const closeDropdown = () => setShowUserDropdown(false);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, mt: 3 }}>
        <PeopleAltIcon color="primary" sx={{ fontSize: 22 }} />
        <SectionTitle>{t("table.route.assignment")}</SectionTitle>
      </Stack>

      <FieldLabel required>{t("table.route.employees")}</FieldLabel>

      <EditOnly>
      <ClickAwayListener onClickAway={closeDropdown}>
        <Box sx={{ position: "relative" }}>
          <TextField
            fullWidth
            placeholder={t("table.route.searchEmployee")}
            value={userSearchText}
            onChange={(e) => {
              onUserSearchTextChange(e.target.value);
              openDropdown();
            }}
            onFocus={openDropdown}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: "rgba(0,0,0,0.26)", fontSize: 20 }} />,
            }}
            sx={userSearchFieldSx}
          />

          {showUserDropdown && filteredUsers.length > 0 && (
            <Paper
              elevation={6}
              sx={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                zIndex: 10,
                maxHeight: 240,
                overflowY: "auto",
                borderBottomLeftRadius: 10,
                borderBottomRightRadius: 10,
                mt: 0.5,
              }}
            >
              {filteredUsers.map((user) => (
                <Box
                  key={user.id}
                  onClick={() => {
                    onAddUser(user);
                    onUserSearchTextChange("");
                    closeDropdown();
                  }}
                  sx={{
                    px: 2,
                    py: 1,
                    cursor: "pointer",
                    fontSize: 14,
                    "&:hover": { backgroundColor: "rgba(25,118,210,0.06)" },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                      {user.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography sx={{ fontSize: 14 }}>{user.name}</Typography>
                  </Stack>
                </Box>
              ))}
            </Paper>
          )}

          {showUserDropdown && filteredUsers.length === 0 && (
            <Paper
              elevation={6}
              sx={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                zIndex: 10,
                borderBottomLeftRadius: 10,
                borderBottomRightRadius: 10,
                mt: 0.5,
                px: 2,
                py: 1.5,
              }}
            >
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                {t("table.route.noEmployees")}
              </Typography>
            </Paper>
          )}
        </Box>
      </ClickAwayListener>
      </EditOnly>

      {selectedUsers.length > 0 && (
        <Stack direction="row" sx={{ mt: 1.5, flexWrap: "wrap", gap: 0.75 }}>
          {selectedUsers.map((user) => (
            <PrimaryChip
              key={user.id}
              label={user.name}
              onDelete={canEdit() ? () => onRemoveUser(user.id) : undefined}
              deleteIcon={<CloseIcon />}
              avatar={
                <Avatar sx={{ width: 22, height: 22, fontSize: 11 }}>
                  {user.name.charAt(0).toUpperCase()}
                </Avatar>
              }
              sx={{
                fontSize: 14,
                paddingY: 2,
                paddingX: 0.4,
              }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default RouteAssignmentSection;